import { useCurrency } from "@shared/selectors/globalSelectors";
import { Progress } from "@shared/ui/progress";
import React, { useState } from "react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../../shared/ui/card";
import { useOutstandingBills, useOutstandingSummary } from "../../hooks";

interface PaymentDetailsTabProps {
  billCycleId: number;
}

export const PaymentDetailsTab: React.FC<PaymentDetailsTabProps> = ({
  billCycleId,
}) => {
  const currency = useCurrency();

  const {
    data: outstandingBills,
    isPending: isOutstandingBillsLoading,
    error: outstandingBillsError,
  } = useOutstandingBills(billCycleId);

  const [outstandingBillsCurrentPage, setOutstandingBillsCurrentPage] =
    useState(1);

  const {
    data: outstandingSummary,
    isPending: isOutstandingSummaryLoading,
    error: outstandingSummaryError,
  } = useOutstandingSummary(billCycleId);

  const columns = [
    {
      key: "remoteConsumerNumber",
      header: "Account",
      width: "w-[100px]",
      render: (value: any, row: any) => {
        return <span className="font-medium">{row.remoteConsumerNumber}</span>;
      },
    },
    {
      key: "fullName",
      header: "Consumer",
    },
    {
      key: "outstandingBalance",
      header: "Amount",
      render: (value: any, row: any) => {
        return (
          <span className="text-right">
            {currency} {row.outstandingBalance}
          </span>
        );
      },
    },
    {
      key: "lastPaymentAmount",
      header: "Last Payment",
    },
  ];

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Payment Summary</CardTitle>
            <CardDescription>Outstanding vs. paid amounts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="card-subheading-text">
                    Total Amount
                  </div>
                  <div className="card-value-text">
                    {currency}{" "}
                    {/* {outstandingSummary?.totalOriginalOutstandingBalance} */}
                    {Number(outstandingSummary?.totalOriginalOutstandingBalance || 0).toFixed(2)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="card-subheading-text">
                    Billing Period
                  </div>
                  <div className="card-value-text">
                    {outstandingSummary?.billingPeriod ?? "NA"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {outstandingSummary?.billingDays}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="card-subheading-text">
                    Paid
                  </div>
                  <div className="card-value-text text-success">
                        {currency} {Number(outstandingSummary?.totalPaidAmount || 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {outstandingSummary?.paidPercentage}%
                  </div>
                </div>
                <div>
                  <div className="card-subheading-text">
                    Outstanding
                  </div>
                  <div className="card-value-text text-warning">
                    {currency} {outstandingSummary?.totalOutstandingBalance}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {outstandingSummary?.outstandingPercentage}%
                  </div>
                </div>
                <div>
                  <div className="card-subheading-text">
                    Service Charges
                  </div>
                  <div className="card-value-text text-primary">
                    {currency} {outstandingSummary?.serviceCharges}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {outstandingSummary?.serviceChargesPercentage}%
                  </div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Payment Rate</span>
                  <span className="text-sm font-medium">
                    {outstandingSummary?.paidPercentage}%
                  </span>
                </div>
                <Progress
                  value={parseFloat(outstandingSummary?.paidPercentage)}
                  className="h-2"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Outstanding Accounts</CardTitle>
            <CardDescription>Highest unpaid balances</CardDescription>
          </CardHeader>
          <CardContent className="">
            <GenericTable
              data={outstandingBills ?? []}
              columns={columns}
              rowKey="id"
              emptyMessage="No outstanding bills found"
              loading={false}
              onPageChange={() => {}}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
